import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {useUpdateEffect} from 'ahooks';
import {ItemWithDualFontsSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-fonts-selector';
import {doesLayoutItemHasDualFonts} from '@Components/poster-editor/library/poster-editor-library';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';
import type {LayoutTypes} from '@PosterWhiteboard/items/layouts/layout.types';

export interface PosterEditorLayoutItemFontPanelProps {
  panelId: string;
  fontFamilyOption?: FontFamilyOptionType;
}

export default function PosterEditorLayoutItemFontPanel({fontFamilyOption = FontFamilyOptionType.ONE, ...props}: PosterEditorLayoutItemFontPanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const onClose = useDefaultPanelClose(props.panelId);
  const layoutStyle = useGetKeyFromTableItem('layoutStyle') as LayoutTypes;

  useEffect((): void => {
    if (layoutStyle === undefined) {
      return;
    }
    if (fontFamilyOption === FontFamilyOptionType.TWO && !activeItem?.isMenu() && !doesLayoutItemHasDualFonts(layoutStyle)) {
      onClose();
    }
  }, [layoutStyle]);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  const getHeaderTextFromOption = (): string => {
    switch (fontFamilyOption) {
      case FontFamilyOptionType.ONLY_FONT:
        return window.i18next.t('pmwjs_font');
      case FontFamilyOptionType.ONE:
        return window.i18next.t('pmjws_font_one');
      case FontFamilyOptionType.TWO:
        return window.i18next.t('pmjws_font_two');
      default:
        return window.i18next.t('pmwjs_font');
    }
  };

  if (!activeItem) {
    return null;
  }

  if (!activeItem.isTableItem() && !activeItem.isMenu()) {
    return null;
  }

  if (fontFamilyOption === FontFamilyOptionType.TWO && !activeItem?.isMenu() && !doesLayoutItemHasDualFonts(layoutStyle)) {
    return null;
  }

  return <ItemWithDualFontsSelector headerText={getHeaderTextFromOption()} font={fontFamilyOption} onClose={onClose} showCloseButton showBackButton={false} />;
}
